import './Show.scss';

import { type FC, lazy, useMemo, useEffect, Suspense } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { sortBy } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import {
  FontFamilyQuery,
  FontFamilyQueryVariables,
} from 'client/graphql/types/operations';

import { Redirect } from 'components/Redirect';
import useLanguage from 'utils/useLanguage';
import * as state from 'state';
import * as Core from 'components/core';
import Hero from './Hero';
import About from './About';
import OpentypeFeatures from './OpentypeFeatures';
import LanguageSupport from './LanguageSupport';
import CharacterSet from './CharacterSet';
import FontFaceDeclarations from './FontFaceDeclarations';
import TrialFontsForm from './TrialFontsForm';

import { sortFontFaces } from 'utils/sorting';
import FamilyHeaderMedia from './FamilyHeaderMedia';
import { UsageExamples } from 'components/UsageExamples';
import { FONT_FAMILY_QUERY } from './FONT_FAMILY_QUERY';
import ColorBar from '../ColorBar';

const TypeTester = lazy(() => import('./TypeTester'));
const Purchase = lazy(() => import('./Purchase'));

export const Show: FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { t } = useTranslation();

  if (slug == null) {
    throw new Error('Missing font family slug');
  }

  const language = useLanguage();

  const { data, loading } = useQuery<FontFamilyQuery, FontFamilyQueryVariables>(
    FONT_FAMILY_QUERY,
    {
      variables: { slug, language },
    },
  );

  const family = data?.fontFamily;

  const sortedTypeTesters = useMemo(() => {
    if (family?.typeTesters == null) {
      return [];
    }

    return sortBy(family.typeTesters, (typeTester) => typeTester.position);
  }, [family]);

  const sortedFontFaces = useMemo(
    () => sortFontFaces(data?.fontFamily?.faces ?? []),
    [data?.fontFamily?.faces],
  );

  // Hide the trial fonts form when navigating away
  useEffect(() => {
    return () => {
      state.trialFontsFormVisible(false);
    };
  }, []);

  if (loading) {
    return (
      <ColorBar>
        <Core.Loader />
      </ColorBar>
    );
  }

  if (family == null) {
    return <Redirect to="/" />;
  }

  return (
    <Core.DocumentTitle title={family.name}>
      <FontFaceDeclarations>
        {family.headerMedia != null && (
          <FamilyHeaderMedia
            media={family.headerMedia}
            aspectRatio={family.headerMediaAspectRatio}
            hasAudio={family.headerMediaHasAudio}
          />
        )}

        {!family.hideFontFacePreviews && !family.conceptFont && (
          <Hero family={family} fontFaces={sortedFontFaces} />
        )}

        <TrialFontsForm />

        <Core.ErrorBoundary fallback={<></>}>
          <div
            style={
              family.backgroundColor
                ? { backgroundColor: family.backgroundColor }
                : {}
            }
          >
            {sortedTypeTesters.map((typeTester) => (
              <TypeTester
                key={typeTester.id}
                typeTester={typeTester}
                faces={sortedFontFaces}
                color={family.textColor}
              />
            ))}
          </div>
        </Core.ErrorBoundary>

        <About family={family} />

        {!family.hideFontFacePreviews && family.conceptFont && (
          <Hero family={family} fontFaces={sortedFontFaces} />
        )}

        <OpentypeFeatures features={family.features} />

        {!family.conceptFont && (
          <LanguageSupport languages={family.supportedLanguages} />
        )}

        {family.characterSetImage && (
          <CharacterSet image={family.characterSetImage} />
        )}

        {family.usageExamples.length > 0 && (
          <div className="FamilyInUse content-wrapper">
            <h2>{family.name} In Use</h2>

            <UsageExamples data={family.usageExamples} />

            <p>
              <Core.Link to="/our-fonts-in-use">
                View in use examples for all families &rarr;
              </Core.Link>
            </p>
          </div>
        )}

        <ColorBar>
          <div className="content-wrapper">
            <h2 id="purchase">
              {t('family.purchaseFamily', { familyName: family.name })}
            </h2>

            <Suspense fallback={<Core.Loader />}>
              <Purchase family={family} />
            </Suspense>
          </div>
        </ColorBar>
      </FontFaceDeclarations>
    </Core.DocumentTitle>
  );
};

Show.displayName = 'Families.Show';

export default Show;
